import type {
  TaskAssignmentResponse,
  TaskDefinitionResponse,
} from 'src/api/ProactApiClient';
import {isEffectivelyOffline} from 'src/offline/connectivity';
import {offlineMutation} from 'src/offline/offlineMutation';
import {hasPendingUploads} from 'src/offline/pendingUploads';
import {applyActionCreate, applyTaskTransition} from './appliers';
import {createDefinition, transitionAssignment} from './api';
import type {OfflineActionCreateRequest} from './queuedTransition';
import {stripQueuedTransition} from './queuedTransition';
import {TASK_OUTBOX_KINDS} from './outboxKinds';
import {TASKS_CONTEXT_KEY} from './snapshotKeys';

export interface TransitionTaskAssignmentArgs {
  assignmentId: number;
  transition: string;
  note: string;
  /** Field time of the completion, kept when the sync happens hours later. */
  occurredAt: string;
}

// Prepared transition `queryFn`: queues the completion while offline so it
// replays with its original time on reconnect.
export const transitionTaskAssignmentMutationFn = offlineMutation<
  TransitionTaskAssignmentArgs,
  TaskAssignmentResponse
>(
  TASK_OUTBOX_KINDS.transition,
  // Direct requests use server time; only queued replay sends occurredAt.
  args =>
    transitionAssignment(args.assignmentId, {
      transition: args.transition,
      note: args.note,
    }),
  {
    contextKey: () => TASKS_CONTEXT_KEY,
    shouldDivert: () => isEffectivelyOffline(),
    applyLocal: applyTaskTransition,
  }
);

// Prepared create-Action `queryFn`: diverts while offline, or when the payload
// carries outbox-only offline-captured inline attachments.
export const createActionMutationFn = offlineMutation<
  OfflineActionCreateRequest,
  TaskDefinitionResponse
>(TASK_OUTBOX_KINDS.create, request => createDefinition(stripQueuedTransition(request)), {
  contextKey: () => TASKS_CONTEXT_KEY,
  shouldDivert: request =>
    isEffectivelyOffline() || hasPendingUploads([request.inlineAttachments]),
  // Queued in the create's transaction so uploads and the create commit together.
  applyLocal: applyActionCreate,
});
